import React, { useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useNavigate } from "react-router-dom";

import axiosInstance from "../../utils/axiosInstance";

import {
  formatCurrency,
  formatDate,
  formatSlotRange,
  formatCenterSlotRange,
  getStatusBadge,
  getPaymentStatusBadge,
} from "../../utils/orderFormatters";

// ── Numbered pagination helper ─────────────────────────────────────────────────
const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
  if (current >= total - 3) return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
};

// ── Report badge ───────────────────────────────────────────────────────────────
const ReportBadge = ({ reportReady }) =>
  reportReady ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-100 text-emerald-700">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
      Ready
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
      Pending
    </span>
  );

const labOrders = () => {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ currentPage: 1, totalPages: 1, total: 0, perPage: 10 });

  // ── Search inputs (not applied until Search button clicked) ───────────────
  const [orderIdInput, setOrderIdInput] = useState("");
  const [patientNameInput, setPatientNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [testNameInput, setTestNameInput] = useState("");
  const [orderDateInput, setOrderDateInput] = useState("");

  // ── Applied search filters ────────────────────────────────────────────────
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [patientNameFilter, setPatientNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [testNameFilter, setTestNameFilter] = useState("");
  const [orderDateFilter, setOrderDateFilter] = useState("");

  // ── Dropdown / date range filters (apply immediately on change) ───────────
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [refCenterFilter, setRefCenterFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [b2bCenterFilter, setB2bCenterFilter] = useState("all");

  // ── Dropdown data ─────────────────────────────────────────────────────────
  const [refCenters, setRefCenters] = useState([]);
  const [venueData, setVenueData] = useState([]);
  const [b2bData, setB2bData] = useState([]);
  const [sourceData, setsourceData] = useState([]);
  const [isLoadingRefCenters, setIsLoadingRefCenters] = useState(false);
  const [isLoadingVenue, setIsLoadingVenue] = useState(false);
  const [isLoadingB2b, setIsLoadingB2b] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const handleMenuToggle = () => setSidebarOpen((s) => !s);
  const handleSidebarClose = () => setSidebarOpen(false);
  const handleViewDetails = (id) => navigate(`/orders/${id}`);

  const isSearchInputActive = () =>
    orderIdInput || patientNameInput || phoneInput || testNameInput || orderDateInput;

  const isFilterActive = () =>
    orderIdFilter || patientNameFilter || phoneFilter || testNameFilter || orderDateFilter ||
    fromDateFilter || toDateFilter ||
    statusFilter !== "all" || paymentStatusFilter !== "all" || sourceFilter !== "all" ||
    refCenterFilter !== "all" || venueFilter !== "all" || b2bCenterFilter !== "all";

  // Apply text search inputs on button click
  const handleSearch = (e) => {
    e?.preventDefault();
    setOrderIdFilter(orderIdInput);
    setPatientNameFilter(patientNameInput);
    setPhoneFilter(phoneInput);
    setTestNameFilter(testNameInput);
    setOrderDateFilter(orderDateInput);
    setPage(1);
  };

  const clearSearchInputs = () => {
    setOrderIdInput(""); setPatientNameInput(""); setPhoneInput("");
    setTestNameInput(""); setOrderDateInput("");
    setOrderIdFilter(""); setPatientNameFilter(""); setPhoneFilter("");
    setTestNameFilter(""); setOrderDateFilter("");
    setPage(1);
  };

  const resetFilters = () => {
    clearSearchInputs();
    setFromDateFilter(""); setToDateFilter("");
    setStatusFilter("all"); setPaymentStatusFilter("all"); setSourceFilter("all");
    setRefCenterFilter("all"); setVenueFilter("all"); setB2bCenterFilter("all");
    setPage(1);
  };

  // ── Dropdown fetches ──────────────────────────────────────────────────────
  const fetchRefCenters = async () => {
    try {
      setIsLoadingRefCenters(true);
      const res = await axiosInstance.get(`${API_BASE}/api/reference-centers`, { withCredentials: true });
      setRefCenters(res.data?.data || []);
    } catch (err) { console.error(err); }
    finally { setIsLoadingRefCenters(false); }
  };

  const fetchVenueAndB2b = async () => {
    try {
      setIsLoadingVenue(true); setIsLoadingB2b(true);
      const [vR, bR, sR] = await Promise.all([
        axiosInstance.get(`${API_BASE}/api/diagnostic-centers`, { withCredentials: true }),
        axiosInstance.get(`${API_BASE}/api/centers`, { withCredentials: true }),
        axiosInstance.get(`${API_BASE}/api/source`, { withCredentials: true }),
      ]);
      setVenueData(vR.data?.data || []);
      setB2bData(bR.data?.data || []);
      setsourceData(sR.data?.data || []);
    } catch (err) { console.error(err); }
    finally { setIsLoadingVenue(false); setIsLoadingB2b(false); }
  };

  // ── Fetch Orders ──────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      setIsLoading(true); setError(null);
      const res = await axiosInstance.get(`${API_BASE}/api/orders/lab-orders`, {
        withCredentials: true,
        params: {
          page, limit: 10,
          orderId: orderIdFilter,
          patientName: patientNameFilter,
          phone: phoneFilter,
          testName: testNameFilter,
          orderDate: orderDateFilter,
          fromDate: fromDateFilter,
          toDate: toDateFilter,
          status: statusFilter === "all" ? "" : statusFilter,
          paymentStatus: paymentStatusFilter === "all" ? "" : paymentStatusFilter,
          source: sourceFilter === "all" ? "" : sourceFilter,
          refCenterId: refCenterFilter === "all" ? "" : refCenterFilter,
          diagnosticCenterId: venueFilter === "all" ? "" : venueFilter,
          centerId: b2bCenterFilter === "all" ? "" : b2bCenterFilter,
        },
      });
      setOrders(res.data?.orders || []);
      setMeta(res.data?.meta || { currentPage: 1, totalPages: 1, total: 0, perPage: 10 });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load orders. Please try again.");
    } finally { setIsLoading(false); }
  };

  useEffect(() => {
    fetchRefCenters(); fetchVenueAndB2b(); fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderIdFilter, patientNameFilter, phoneFilter, testNameFilter, orderDateFilter,
    fromDateFilter, toDateFilter,
    statusFilter, paymentStatusFilter, sourceFilter,
    refCenterFilter, venueFilter, b2bCenterFilter,
    page,
  ]);

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputCls = "block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs";
  const selectCls = "w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs";
  const labelCls = "block text-xs font-medium text-gray-700 mb-1.5";

  const pageNumbers = getPageNumbers(meta.currentPage, meta.totalPages);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
   <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />


      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4 lg:p-6">

          {/* Page title */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="ShoppingBag" size={18} className="text-blue-600" />
              </div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Lab Orders</h1>
            </div>
            <p className="text-sm text-gray-500 mt-1 ml-10">Manage laboratory test orders, track status, and view reports</p>
          </div>

          {/* ── Filter Panel ─────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-6">
            <form onSubmit={handleSearch} className="space-y-4">

              {/* Row 1 — text search */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-12 gap-3 items-end">

                <div className="xl:col-span-2">
                  <label className={labelCls}>Order ID</label>
                  <div className="relative">
                    <Icon name="Hash" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="e.g. 1023" value={orderIdInput}
                      onChange={(e) => setOrderIdInput(e.target.value)} className={`${inputCls} pl-8`} />
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Patient Name</label>
                  <div className="relative">
                    <Icon name="User" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="Full name..." value={patientNameInput}
                      onChange={(e) => setPatientNameInput(e.target.value)} className={`${inputCls} pl-8`} />
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Phone Number</label>
                  <div className="relative">
                    <Icon name="Phone" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="Mobile number..." value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)} className={`${inputCls} pl-8`} />
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Test Name</label>
                  <div className="relative">
                    <Icon name="FlaskConical" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="Test / package..." value={testNameInput}
                      onChange={(e) => setTestNameInput(e.target.value)} className={`${inputCls} pl-8`} />
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Order Date</label>
                  <div className="relative">
                    <Icon name="CalendarDays" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="date" value={orderDateInput}
                      onChange={(e) => setOrderDateInput(e.target.value)} className={`${inputCls} pl-8`} />
                  </div>
                </div>

                {/* Search + clear */}
                <div className="xl:col-span-2 flex items-end gap-2">
                  <button type="submit"
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-[9px] bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
                    <Icon name="Search" size={13} />
                    Search
                  </button>
                  {isSearchInputActive() && (
                    <button type="button" onClick={clearSearchInputs}
                      className="p-2 border border-gray-300 rounded-lg text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors">
                      <Icon name="X" size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2 — date range + dropdowns */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-12 gap-3 items-end">

                <div className="xl:col-span-2">
                  <label className={labelCls}>From Date</label>
                  <input type="date" value={fromDateFilter}
                    onChange={(e) => { setFromDateFilter(e.target.value); setPage(1); }} className={inputCls} />
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>To Date</label>
                  <input type="date" value={toDateFilter}
                    onChange={(e) => { setToDateFilter(e.target.value); setPage(1); }} className={inputCls} />
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Venue</label>
                  <select value={venueFilter} onChange={(e) => { setVenueFilter(e.target.value); setPage(1); }}
                    disabled={isLoadingVenue} className={selectCls}>
                    <option value="all">All Venues</option>
                    {venueData.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>B2B Center</label>
                  <select value={b2bCenterFilter} onChange={(e) => { setB2bCenterFilter(e.target.value); setPage(1); }}
                    disabled={isLoadingB2b} className={selectCls}>
                    <option value="all">All B2B Centers</option>
                    {b2bData.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Source</label>
                  <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }} className={selectCls}>
                    <option value="all">All</option>
                    {sourceData.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                    <option value="app">Online</option>
                  </select>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Status</label>
                  <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className={selectCls}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="sample_collected">Sample Collected</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Row 3 — ref center + payment + reset */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-12 gap-3 items-end">

                <div className="xl:col-span-4">
                  <label className={labelCls}>Ref-Center</label>
                  <select value={refCenterFilter} onChange={(e) => { setRefCenterFilter(e.target.value); setPage(1); }}
                    disabled={isLoadingRefCenters} className={selectCls}>
                    <option value="all">All Ref Centers</option>
                    {refCenters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                <div className="xl:col-span-2">
                  <label className={labelCls}>Payment</label>
                  <select value={paymentStatusFilter} onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(1); }} className={selectCls}>
                    <option value="all">All</option>
                    <option value="pending">Due</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>

                <div className="xl:col-span-6 flex justify-end items-end">
                  <button type="button" onClick={resetFilters}
                    disabled={!isFilterActive() && !isSearchInputActive()}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Icon name="RotateCcw" size={12} />
                    Reset All
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* ── Table ─────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

            {/* Table top bar */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Package" size={16} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Order Records</h3>
                <p className="text-xs text-gray-500">{meta.total || 0} orders found</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {["#", "Order Details", "Patient", "Ref-Center", "Venue", "B2B Center", "Schedule", "Amount", "Vendor", "Report", "Status"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {isLoading ? (
                    <tr>
                      <td colSpan="11" className="py-12 text-center">
                        <div className="inline-flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                          <span className="text-xs text-gray-400">Loading orders...</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="11" className="py-12 text-center">
                        <div className="inline-flex flex-col items-center gap-2">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <Icon name="AlertCircle" size={20} className="text-red-500" />
                          </div>
                          <p className="text-sm font-medium text-red-600">Error loading orders</p>
                          <p className="text-xs text-red-400">{error}</p>
                          <button onClick={fetchOrders} className="mt-1 px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
                            Try Again
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="py-12 text-center">
                        <div className="inline-flex flex-col items-center gap-2">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <Icon name="ShoppingBag" size={20} className="text-gray-400" />
                          </div>
                          <p className="text-sm font-medium text-gray-600">No orders found</p>
                          <p className="text-xs text-gray-400">Try adjusting your filters</p>
                          {(isFilterActive() || isSearchInputActive()) && (
                            <button onClick={resetFilters} className="mt-1 px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => (
                      <tr key={order.id} onClick={() => handleViewDetails(order.id)}
                        className="cursor-pointer hover:bg-blue-50/30 transition-colors">

                        {/* # */}
                        <td className="px-4 py-3 text-gray-400 font-mono">
                          {String((meta.currentPage - 1) * meta.perPage + index + 1).padStart(2, "0")}
                        </td>

                        {/* Order Details */}
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            <p className="font-semibold text-gray-900">ORD-{order.id}</p>
                            <div className="flex items-center gap-1">
                              <div className={`w-1.5 h-1.5 rounded-full ${order.isHomeSample ? "bg-emerald-500" : "bg-blue-500"}`} />
                              <span className={`text-[10px] font-medium ${order.isHomeSample ? "text-emerald-600" : "text-blue-600"}`}>
                                {order.isHomeSample ? "Home Collection" : "Center Visit"}
                              </span>
                            </div>
                            <p className="text-[10px] text-gray-400">
                              {new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </td>

                        {/* Patient */}
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            {(order.orderMembers?.length > 0
                              ? order.orderMembers.map((m) => m.patient?.fullName).filter(Boolean)
                              : [order.patient?.fullName].filter(Boolean)
                            ).map((name, i) => (
                              <p key={i} className="font-medium text-gray-900">{name}</p>
                            ))}
                            <p className="text-[10px] text-gray-500">{order.patient?.contactNo || "—"}</p>
                            <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{order.source || "app"}</span>
                          </div>
                        </td>

                        {/* Ref Center */}
                        <td className="px-4 py-3">
                          {order.refCenter || order.doctor ? (
                            <div className="flex flex-col gap-1">
                              {order.doctor && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-5 bg-blue-50 rounded flex-shrink-0 flex items-center justify-center">
                                    <Icon name="User" size={11} className="text-blue-600" />
                                  </div>
                                  <span className="text-gray-800 text-sm">
                                    {order.doctor.initial ? `${order.doctor.initial} ` : ""}{order.doctor.name}
                                  </span>
                                </div>
                              )}
                              {order.refCenter && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-5 bg-amber-50 rounded flex-shrink-0 flex items-center justify-center">
                                    <Icon name="Building" size={11} className="text-amber-600" />
                                  </div>
                                  <span className="text-gray-600 text-xs">{order.refCenter.name}</span>
                                </div>
                              )}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Venue */}
                        <td className="px-4 py-3">
                          {order.diagnosticCenter
                            ? <span className="text-gray-700">{order.diagnosticCenter.name}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>

                        {/* B2B Center */}
                        <td className="px-4 py-3">
                          {order.center
                            ? <span className="text-gray-700">{order.center.name}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Schedule */}
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <Icon name="Calendar" size={10} className="text-gray-400" />
                              <span className="text-[10px] text-gray-700">{formatDate(order.date)}</span>
                            </div>
                            {order.slot && (
                              <div className="flex items-center gap-1">
                                <Icon name="Clock" size={10} className="text-gray-400" />
                                <span className="text-[10px] text-gray-500">{formatSlotRange(order.slot)}</span>
                              </div>
                            )}
                            {order.centerSlot && (
                              <div className="flex items-center gap-1">
                                <Icon name="Clock" size={10} className="text-gray-400" />
                                <span className="text-[10px] text-gray-500">{formatCenterSlotRange(order.centerSlot)}</span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <p className="font-semibold text-gray-900">{formatCurrency(order.finalAmount || 0)}</p>
                            {order.discount > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] line-through text-gray-400">{formatCurrency(order.totalAmount)}</span>
                                <span className="text-[9px] bg-green-100 text-green-700 px-1 py-0.5 rounded">-{formatCurrency(order.discount)}</span>
                              </div>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-medium ${getPaymentStatusBadge(order.paymentStatus)}`}>
                              {order.paymentStatus === "pending" ? "DUE" : order.paymentStatus}
                            </span>
                            {order.paymentMode && <p className="text-[9px] text-gray-400">via {order.paymentMode}</p>}
                          </div>
                        </td>

                        {/* Vendor */}
                        <td className="px-4 py-3">
                          <span className="text-[10px] text-gray-500">{order.vendor?.name || "—"}</span>
                        </td>

                        {/* ✅ Report Status */}
                        <td className="px-4 py-3">
                          <ReportBadge reportReady={order.reportReady} />
                        </td>

                        {/* Order Status */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium ${getStatusBadge(order.status)}`}>
                            <span className={`w-1 h-1 rounded-full mr-1.5 ${
                              order.status === "completed" ? "bg-emerald-500" :
                              order.status === "cancelled" ? "bg-rose-500" :
                              order.status === "pending" ? "bg-amber-500" :
                              order.status === "sample_collected" ? "bg-violet-500" :
                              order.status === "confirmed" ? "bg-sky-500" : "bg-gray-400"
                            }`} />
                            {order.status?.replaceAll("_", " ") || "Pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Numbered Pagination ──────────────────────────────────────── */}
            <div className="px-5 py-4 bg-gray-50/60 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

              {/* Count text */}
              <p className="text-xs text-gray-500">
                Showing{" "}
                <span className="font-semibold text-gray-800">
                  {meta.total === 0 ? 0 : (meta.currentPage - 1) * meta.perPage + 1}
                </span>
                {" – "}
                <span className="font-semibold text-gray-800">
                  {Math.min(meta.currentPage * meta.perPage, meta.total)}
                </span>
                {" of "}
                <span className="font-semibold text-gray-800">{meta.total}</span> orders
              </p>

              {/* Page buttons */}
              {meta.totalPages > 1 && (
                <div className="flex items-center gap-1">

                  {/* ← Prev */}
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500
                      hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Icon name="ChevronLeft" size={14} />
                  </button>

                  {/* Page numbers */}
                  {pageNumbers.map((p, i) =>
                    p === "..." ? (
                      <span key={`dots-${i}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-xs select-none">
                        ···
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors
                          ${p === meta.currentPage
                            ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                            : "border border-gray-300 text-gray-600 hover:bg-white hover:border-gray-400"
                          }`}>
                        {p}
                      </button>
                    )
                  )}

                  {/* → Next */}
                  <button
                    onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                    disabled={page === meta.totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500
                      hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Icon name="ChevronRight" size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default labOrders;